import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TourInclusions = ({ inclusions, exclusions, policies }) => {
  const [activeTab, setActiveTab] = useState('inclusions');

  const tabs = [
    { id: 'inclusions', label: 'What\'s Included', icon: 'Check' },
    { id: 'exclusions', label: 'What\'s Not Included', icon: 'X' },
    { id: 'policies', label: 'Policies', icon: 'FileText' }
  ];

  const renderInclusionItem = (item, isIncluded = true) => (
    <div key={item?.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/30 transition-colors">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
        isIncluded ? 'bg-success/10' : 'bg-error/10'
      }`}>
        <Icon 
          name={isIncluded ? "Check" : "X"} 
          size={14} 
          className={isIncluded ? 'text-success' : 'text-error'} 
        />
      </div>
      <div className="flex-1">
        <div className="font-medium text-foreground">{item?.title}</div>
        {item?.description && (
          <div className="text-sm text-muted-foreground mt-1">{item?.description}</div>
        )}
        {item?.details && (
          <div className="text-xs text-muted-foreground mt-2 italic">{item?.details}</div>
        )}
      </div>
    </div>
  );

  const renderPolicySection = (section) => (
    <div key={section?.id} className="mb-6">
      <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
        <Icon name={section?.icon} size={18} />
        <span>{section?.title}</span>
      </h4>
      <div className="space-y-3">
        {section?.items?.map((item, index) => (
          <div key={index} className="p-4 bg-muted/30 rounded-lg">
            <div className="font-medium text-foreground mb-2">{item?.title}</div>
            <div className="text-sm text-muted-foreground leading-relaxed">{item?.description}</div>
            {item?.note && (
              <div className="text-xs text-primary mt-2 font-medium">{item?.note}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white">
      <h2 className="text-2xl font-bold text-foreground mb-6">Tour Details</h2>
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-muted/30 p-1 rounded-lg">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab?.id
                ? 'bg-white text-primary shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'inclusions' && (
          <div className="space-y-2">
            <div className="mb-4">
              <p className="text-muted-foreground">
                Everything that's included in your tour package:
              </p>
            </div>
            {inclusions?.map((item) => renderInclusionItem(item, true))}
          </div>
        )}

        {activeTab === 'exclusions' && (
          <div className="space-y-2">
            <div className="mb-4">
              <p className="text-muted-foreground">
                Items and services not included in the tour price:
              </p>
            </div>
            {exclusions?.map((item) => renderInclusionItem(item, false))}
          </div>
        )}

        {activeTab === 'policies' && (
          <div>
            {policies?.map((section) => renderPolicySection(section))}
          </div>
        )}
      </div>
      {/* Important Notes */}
      <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-900 mb-2">Important Information</h4>
            <ul className="space-y-1 text-sm text-yellow-800">
              <li>• Please arrive at the meeting point 15 minutes before departure</li>
              <li>• Bring comfortable walking shoes and weather-appropriate clothing</li>
              <li>• Valid ID required for all participants</li>
              <li>• Tour may be cancelled due to weather conditions with full refund</li>
              <li>• Photography restrictions may apply at certain locations</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Contact Support */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-primary mb-1">Questions about inclusions?</h4>
            <p className="text-sm text-primary/80">Our support team is here to help clarify any details</p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
            <Icon name="MessageCircle" size={16} />
            <span className="text-sm font-medium">Contact Support</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourInclusions;